import { useEffect, useRef, useState } from "react";
import { Button } from "primereact/button";
import { Column } from "primereact/column";
import { DataTable } from "primereact/datatable";
import { Dialog } from "primereact/dialog";
import { InputText } from "primereact/inputtext";
import { Tag } from "primereact/tag";
import WorkTrackingAnalyticsService from "../../services/WorkTrackingAnalyticsService";

const DAY_IN_MS = 24 * 60 * 60 * 1000;

const eventTypeLabels = {
  page_view: "Sayfa görüntüleme",
  summary_card_click: "Özet kartı seçimi",
  tab_select: "Sekme seçimi",
  filter_apply: "Filtre uygulama",
  search: "Arama",
  detail_click: "Detay görüntüleme",
  primary_action_click: "Ana işlem seçimi",
};

const workItemTypeLabels = {
  COLLECTION: "Tahsilat",
  PAYMENT: "Ödeme",
  DOCUMENT: "Çek / senet",
  INSTALLMENT: "Kart taksiti",
  STOCK: "Stok",
};

const sourceTypeLabels = {
  CUSTOMER_RECEIVABLE: "Müşteri alacağı",
  SUPPLIER_PAYABLE: "Tedarikçi borcu",
  CHEQUE: "Çek",
  PROMISSORY_NOTE: "Senet",
  CREDIT_CARD_INSTALLMENT: "Kredi kartı taksiti",
  CRITICAL_STOCK: "Kritik stok",
};

const periodLabels = {
  priority: "Öncelikli işler",
  all: "Tümü",
  problem: "Sorunlu evrak",
  overdue: "Gecikmiş",
  today: "Bugün",
  seven_days: "7 gün",
  thirty_days: "30 gün",
};

const categoryLabels = {
  all: "Tümü",
  collection: "Tahsilatlar",
  payment: "Ödemeler",
  document: "Çek ve senetler",
  installment: "Kart taksitleri",
  stock: "Stok",
  dashboard: "Ana sayfa",
};

const padDatePart = (value) => String(value).padStart(2, "0");

const toDateInputValue = (date) =>
  `${date.getFullYear()}-${padDatePart(date.getMonth() + 1)}-${padDatePart(date.getDate())}`;

const createDefaultDateRange = () => {
  const endDate = new Date();
  const startDate = new Date(endDate);
  startDate.setDate(startDate.getDate() - 29);

  return {
    startDate: toDateInputValue(startDate),
    endDate: toDateInputValue(endDate),
  };
};

const parseDateInput = (value) => {
  const [year, month, day] = (value || "").split("-").map(Number);
  return year && month && day ? Date.UTC(year, month - 1, day) : Number.NaN;
};

const validateDateRange = ({ startDate, endDate }) => {
  const start = parseDateInput(startDate);
  const end = parseDateInput(endDate);

  if (!Number.isFinite(start) || !Number.isFinite(end)) {
    return "Başlangıç ve bitiş tarihlerini seçin.";
  }

  if (end < start) {
    return "Bitiş tarihi başlangıç tarihinden önce olamaz.";
  }

  const inclusiveDayCount = Math.floor((end - start) / DAY_IN_MS) + 1;
  if (inclusiveDayCount > 180) {
    return "En fazla 180 günlük bir tarih aralığı seçebilirsiniz.";
  }

  return null;
};

const unwrapServiceResult = (result) => {
  if (result?.success === false) {
    throw new Error(result.message || "Kullanım verisi alınamadı.");
  }

  const payload = result?.data ?? result;
  if (payload?.success === false) {
    throw new Error(payload.message || "Kullanım verisi alınamadı.");
  }

  return payload?.data ?? payload ?? {};
};

const normalizePagedResult = (result, collectionKeys) => {
  const payload = unwrapServiceResult(result);
  let items = Array.isArray(payload) ? payload : null;

  if (!items) {
    for (const key of collectionKeys) {
      if (Array.isArray(payload?.[key])) {
        items = payload[key];
        break;
      }
    }
  }

  items = items || [];
  const totalCount = Number(
    payload?.totalCount ?? payload?.totalRecords ?? payload?.count ?? items.length
  );

  return {
    items,
    totalCount: Number.isFinite(totalCount) ? totalCount : items.length,
  };
};

const getErrorMessage = (error, fallback) =>
  error?.response?.data?.message || error?.message || fallback;

const getValue = (row, keys, fallback = null) => {
  for (const key of keys) {
    if (row?.[key] !== undefined && row?.[key] !== null) return row[key];
  }
  return fallback;
};

const formatNumber = (value) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed.toLocaleString("tr-TR") : "0";
};

const formatDateTime = (value) => {
  if (!value) return "-";
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? "-" : date.toLocaleString("tr-TR");
};

const translatedValue = (value, labels) => {
  if (!value) return "-";
  return labels[value] || value;
};

const hasUserIdentity = (row) =>
  getValue(row, ["tenantId", "companyId"]) !== null &&
  getValue(row, ["userId", "id"]) !== null;

const getUserStatusMeta = (row) => {
  const value = getValue(row, ["isUserActive", "isActive"]);
  if (value === true || value === "true") return { label: "Aktif", severity: "success" };
  if (value === false || value === "false") return { label: "Pasif", severity: "danger" };
  return { label: "Silinmiş", severity: "secondary" };
};

export default function WorkTrackingUserActivityTab() {
  const defaultRange = useRef(createDefaultDateRange()).current;
  const requestSequence = useRef(0);
  const eventRequestSequence = useRef(0);
  const [filterDraft, setFilterDraft] = useState({ ...defaultRange, search: "" });
  const [filters, setFilters] = useState({ ...defaultRange, search: "" });
  const [filterError, setFilterError] = useState("");
  const [users, setUsers] = useState([]);
  const [totalUsers, setTotalUsers] = useState(0);
  const [first, setFirst] = useState(0);
  const [rows, setRows] = useState(10);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [selectedUser, setSelectedUser] = useState(null);
  const [eventsDialogVisible, setEventsDialogVisible] = useState(false);
  const [events, setEvents] = useState([]);
  const [totalEvents, setTotalEvents] = useState(0);
  const [eventFirst, setEventFirst] = useState(0);
  const [eventRows, setEventRows] = useState(10);
  const [eventsLoading, setEventsLoading] = useState(false);
  const [eventsError, setEventsError] = useState("");

  useEffect(() => {
    const sequence = ++requestSequence.current;
    let active = true;

    const loadUsers = async () => {
      setLoading(true);
      setError("");

      try {
        const result = await WorkTrackingAnalyticsService.getUsers({
          startDate: filters.startDate,
          endDate: filters.endDate,
          search: filters.search || undefined,
          page: Math.floor(first / rows) + 1,
          pageSize: rows,
        });
        const page = normalizePagedResult(result, ["items", "users", "results"]);

        if (active && sequence === requestSequence.current) {
          const normalizedUsers = page.items.map((row, index) => {
            const tenantId = getValue(row, ["tenantId", "companyId"]);
            const userId = getValue(row, ["userId", "id"]);
            return {
              ...row,
              _dataKey:
                tenantId !== null && userId !== null
                  ? `${tenantId}:${userId}`
                  : `kimlik-yok:${first + index}`,
            };
          });
          setUsers(normalizedUsers);
          setTotalUsers(page.totalCount);
        }
      } catch (loadError) {
        if (active && sequence === requestSequence.current) {
          setUsers([]);
          setTotalUsers(0);
          setError(
            getErrorMessage(loadError, "Kullanıcı bazlı İş Takibi verileri yüklenemedi.")
          );
        }
      } finally {
        if (active && sequence === requestSequence.current) setLoading(false);
      }
    };

    loadUsers();
    return () => {
      active = false;
    };
  }, [filters, first, rows]);

  const applyFilters = (event) => {
    event.preventDefault();
    const dateError = validateDateRange(filterDraft);
    setFilterError(dateError || "");
    if (dateError) return;

    setFirst(0);
    setFilters({
      startDate: filterDraft.startDate,
      endDate: filterDraft.endDate,
      search: filterDraft.search.trim(),
    });
  };

  const clearSearch = () => {
    setFilterDraft((current) => ({ ...current, search: "" }));
    setFilterError("");
    setFirst(0);
    setFilters({
      startDate: filters.startDate,
      endDate: filters.endDate,
      search: "",
    });
  };

  const loadUserEvents = async (user, nextFirst, nextRows) => {
    const tenantId = getValue(user, ["tenantId", "companyId"]);
    const userId = getValue(user, ["userId", "id"]);
    if (tenantId === null || userId === null) {
      setEvents([]);
      setTotalEvents(0);
      setEventsError("Kullanıcı veya işletme kimliği bulunamadı.");
      return;
    }

    const sequence = ++eventRequestSequence.current;
    setEventsLoading(true);
    setEventsError("");

    try {
      const result = await WorkTrackingAnalyticsService.getUserEvents(tenantId, userId, {
        startDate: filters.startDate,
        endDate: filters.endDate,
        page: Math.floor(nextFirst / nextRows) + 1,
        pageSize: nextRows,
      });
      const page = normalizePagedResult(result, ["items", "events", "results"]);

      if (sequence === eventRequestSequence.current) {
        setEvents(page.items);
        setTotalEvents(page.totalCount);
      }
    } catch (loadError) {
      if (sequence === eventRequestSequence.current) {
        setEvents([]);
        setTotalEvents(0);
        setEventsError(getErrorMessage(loadError, "Kullanıcının olayları yüklenemedi."));
      }
    } finally {
      if (sequence === eventRequestSequence.current) setEventsLoading(false);
    }
  };

  const openEventsDialog = (user) => {
    setSelectedUser(user);
    setEvents([]);
    setTotalEvents(0);
    setEventFirst(0);
    setEventsDialogVisible(true);
    loadUserEvents(user, 0, eventRows);
  };

  const closeEventsDialog = () => {
    eventRequestSequence.current += 1;
    setEventsDialogVisible(false);
    setEventsLoading(false);
    setSelectedUser(null);
  };

  const userName = (row) =>
    getValue(row, ["fullName", "userFullName", "userName", "name"], "İsimsiz kullanıcı");

  const selectedUserName = selectedUser ? userName(selectedUser) : "Kullanıcı";

  return (
    <div className="space-y-4">
      <div>
        <h3 className="m-0 text-base font-black text-slate-800">
          Kullanıcı Bazlı İş Takibi Kullanımı
        </h3>
        <p className="mb-0 mt-1 text-xs font-medium text-slate-500">
          Kullanıcıların İş Takibi ekranındaki hareketlerini en fazla 180 günlük dönem için inceleyin.
          Anonim geri bildirim olayları kullanıcı bazlı görünümde yer almaz.
        </p>
      </div>

      <form
        onSubmit={applyFilters}
        className="grid gap-3 rounded-2xl border border-slate-200 bg-slate-50 p-4 md:grid-cols-2 xl:grid-cols-[minmax(150px,190px)_minmax(150px,190px)_minmax(240px,1fr)_auto] xl:items-end"
        aria-label="Kullanıcı kullanım filtreleri"
      >
        <div className="flex flex-col gap-2">
          <label
            htmlFor="work-tracking-user-start-date"
            className="text-xs font-bold text-slate-600"
          >
            Başlangıç tarihi
          </label>
          <InputText
            id="work-tracking-user-start-date"
            type="date"
            value={filterDraft.startDate}
            max={filterDraft.endDate || toDateInputValue(new Date())}
            onChange={(event) =>
              setFilterDraft((current) => ({ ...current, startDate: event.target.value }))
            }
            className="w-full"
            required
          />
        </div>

        <div className="flex flex-col gap-2">
          <label
            htmlFor="work-tracking-user-end-date"
            className="text-xs font-bold text-slate-600"
          >
            Bitiş tarihi
          </label>
          <InputText
            id="work-tracking-user-end-date"
            type="date"
            value={filterDraft.endDate}
            min={filterDraft.startDate || undefined}
            max={toDateInputValue(new Date())}
            onChange={(event) =>
              setFilterDraft((current) => ({ ...current, endDate: event.target.value }))
            }
            className="w-full"
            required
          />
        </div>

        <div className="flex flex-col gap-2">
          <label
            htmlFor="work-tracking-user-search"
            className="text-xs font-bold text-slate-600"
          >
            Kullanıcı ara
          </label>
          <span className="p-input-icon-left w-full">
            <i className="pi pi-search" aria-hidden="true" />
            <InputText
              id="work-tracking-user-search"
              type="search"
              value={filterDraft.search}
              onChange={(event) =>
                setFilterDraft((current) => ({ ...current, search: event.target.value }))
              }
              placeholder="Ad, e-posta veya işletme ara..."
              className="w-full"
            />
          </span>
        </div>

        <div className="flex flex-wrap gap-2">
          <Button type="submit" label="Uygula" icon="pi pi-filter" loading={loading} />
          {(filterDraft.search || filters.search) && (
            <Button
              type="button"
              label="Temizle"
              icon="pi pi-times"
              outlined
              severity="secondary"
              onClick={clearSearch}
            />
          )}
        </div>

        {filterError && (
          <div className="md:col-span-2 xl:col-span-4" role="alert">
            <small className="font-semibold text-red-600">{filterError}</small>
          </div>
        )}
      </form>

      {error && (
        <div
          className="flex items-center justify-between gap-3 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm font-semibold text-red-700"
          role="alert"
        >
          <span>{error}</span>
          <Button
            type="button"
            label="Tekrar Dene"
            icon="pi pi-refresh"
            text
            severity="danger"
            onClick={() => setFilters((current) => ({ ...current }))}
          />
        </div>
      )}

      <DataTable
        value={users}
        dataKey="_dataKey"
        loading={loading}
        lazy
        paginator
        first={first}
        rows={rows}
        totalRecords={totalUsers}
        rowsPerPageOptions={[10, 20, 50]}
        onPage={(event) => {
          setFirst(event.first);
          setRows(event.rows);
        }}
        emptyMessage={
          loading ? "Yükleniyor..." : "Seçilen dönemde kullanıcı etkinliği bulunamadı."
        }
        responsiveLayout="stack"
        breakpoint="1100px"
        className="p-datatable-sm overflow-hidden rounded-2xl border border-slate-200"
        paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink RowsPerPageDropdown CurrentPageReport"
        currentPageReportTemplate="{first} - {last} / {totalRecords} kullanıcı"
      >
        <Column
          header="Kullanıcı"
          body={(row) => (
            <div>
              <strong className="block text-sm text-slate-800">{userName(row)}</strong>
              <span className="text-xs text-slate-500">
                {getValue(row, ["email", "userEmail"], "-")}
              </span>
            </div>
          )}
        />
        <Column
          header="İşletme"
          body={(row) => getValue(row, ["tenantName", "companyName"], "-")}
        />
        <Column
          header="Rol"
          body={(row) => (
            <Tag value={getValue(row, ["userRole", "role"], "-")} severity="secondary" />
          )}
        />
        <Column
          header="Durum"
          body={(row) => {
            const status = getUserStatusMeta(row);
            return <Tag value={status.label} severity={status.severity} />;
          }}
        />
        <Column
          header="Toplam Olay"
          body={(row) =>
            formatNumber(getValue(row, ["totalEvents", "totalEventCount", "eventCount"], 0))
          }
        />
        <Column
          header="Ziyaret"
          body={(row) =>
            formatNumber(getValue(row, ["visitCount", "pageViewCount", "visits"], 0))
          }
        />
        <Column
          header="Ana Aksiyon"
          body={(row) =>
            formatNumber(
              getValue(row, ["actionClickCount", "primaryActionClickCount", "actionCount"], 0)
            )
          }
        />
        <Column
          header="Filtre + Arama"
          body={(row) =>
            formatNumber(
              getValue(row, ["filterAndSearchCount", "filterSearchCount"], 0)
            )
          }
        />
        <Column
          header="Detay"
          body={(row) =>
            formatNumber(getValue(row, ["detailClickCount", "detailCount"], 0))
          }
        />
        <Column
          header="Son Hareket"
          body={(row) =>
            formatDateTime(getValue(row, ["lastEventDate", "lastActivityDate", "lastEventAt"]))
          }
        />
        <Column
          header="Olaylar"
          body={(row) => (
            <Button
              type="button"
              label={hasUserIdentity(row) ? "Olayları Gör" : "Kimlik Yok"}
              icon="pi pi-list"
              outlined
              disabled={!hasUserIdentity(row)}
              className="p-button-sm whitespace-nowrap"
              onClick={() => openEventsDialog(row)}
              aria-label={`${userName(row)} kullanıcısının olaylarını görüntüle`}
            />
          )}
        />
      </DataTable>

      <Dialog
        visible={eventsDialogVisible}
        header={`${selectedUserName} — İş Takibi Olayları`}
        modal
        maximizable
        style={{ width: "min(1200px, 96vw)" }}
        onHide={closeEventsDialog}
        footer={
          <Button type="button" label="Kapat" icon="pi pi-times" text onClick={closeEventsDialog} />
        }
      >
        <div className="mb-3 rounded-xl bg-slate-50 px-4 py-3 text-xs font-semibold text-slate-600">
          Dönem: {new Date(`${filters.startDate}T00:00:00`).toLocaleDateString("tr-TR")} –{" "}
          {new Date(`${filters.endDate}T00:00:00`).toLocaleDateString("tr-TR")}
        </div>

        {eventsError && (
          <div
            className="mb-3 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm font-semibold text-red-700"
            role="alert"
          >
            {eventsError}
          </div>
        )}

        <DataTable
          value={events}
          dataKey="id"
          loading={eventsLoading}
          lazy
          paginator
          first={eventFirst}
          rows={eventRows}
          totalRecords={totalEvents}
          rowsPerPageOptions={[10, 20, 50]}
          onPage={(event) => {
            setEventFirst(event.first);
            setEventRows(event.rows);
            loadUserEvents(selectedUser, event.first, event.rows);
          }}
          emptyMessage={
            eventsLoading ? "Yükleniyor..." : "Bu kullanıcı için olay kaydı bulunamadı."
          }
          responsiveLayout="stack"
          breakpoint="1100px"
          className="p-datatable-sm"
          paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink RowsPerPageDropdown CurrentPageReport"
          currentPageReportTemplate="{first} - {last} / {totalRecords} olay"
        >
          <Column
            header="Olay Türü"
            body={(row) => (
              <Tag
                value={translatedValue(getValue(row, ["eventType"]), eventTypeLabels)}
                severity="info"
              />
            )}
          />
          <Column
            header="İş Türü"
            body={(row) =>
              translatedValue(getValue(row, ["workItemType"]), workItemTypeLabels)
            }
          />
          <Column
            header="Kaynak"
            body={(row) => translatedValue(getValue(row, ["sourceType"]), sourceTypeLabels)}
          />
          <Column
            header="Dönem"
            body={(row) =>
              translatedValue(getValue(row, ["selectedPeriod", "period"]), periodLabels)
            }
          />
          <Column
            header="Kategori"
            body={(row) =>
              translatedValue(getValue(row, ["selectedCategory", "category"]), categoryLabels)
            }
          />
          <Column
            header="Tarih"
            body={(row) =>
              formatDateTime(getValue(row, ["createdDate", "eventDate", "occurredAt"]))
            }
          />
        </DataTable>
      </Dialog>
    </div>
  );
}
